import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bitcoin, Timer, TrendingUp, LogOut, Settings, User, Users, Share2, Trash2, BarChart3, Shield, Mail, Building2, Trophy, Zap, DollarSign, ArrowDownUp, Music, Target, FileText, Menu, X, ChevronDown, Download, PlayCircle
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Switch } from '../ui/switch';
import { formatElapsedTime } from '../../utils/formatters';


// Exchange colors for visual identification
const EXCHANGE_COLORS = {
  coinbase: '#0052FF',
  binance: '#F3BA2F',
  kraken: '#5741D9',
  crypto_com: '#002D74'
};

/**
 * Dashboard Header Component
 * Contains logo, mode toggle, user info, and navigation
 * Layout: Two rows
 *   - Top: Logo/Title (left) | Users badge, Admin, Share (right)
 *   - Bottom: Status indicators and navigation buttons
 */
const DashboardHeader = ({
  user,
  tradingMode,
  isConnected,
  coinbaseConnected,
  botStatus,
  activeUsersCount,
  lifetimeProfit,
  lifetimeProfitResetDate,
  runTimer,
  isAdmin,
  accessLevel,
  unreadMessages,
  activeExchange,
  onModeChange,
  onResetLifetimeProfit,
  onResetSessionBalance,
  onClearTradeHistory,
  onShare,
  onLogout,
  onManualTrade
}) => {
  const navigate = useNavigate();

  // New user detection — flash red/green and play ahooga
  const prevCountRef = useRef(null);
  const [flashing, setFlashing] = useState(false);
  const flashTimerRef = useRef(null);

  useEffect(() => {
    if (activeUsersCount == null || activeUsersCount <= 0) return;
    // Skip initial load
    if (prevCountRef.current === null) {
      prevCountRef.current = activeUsersCount;
      return;
    }
    // Detect increase
    if (activeUsersCount > prevCountRef.current) {
      setFlashing(true);
      // Play ahooga twice
      try {
        const audio1 = new Audio('/ahooga.mp3');
        audio1.volume = 0.5;
        audio1.play().catch(() => {});
        setTimeout(() => {
          const audio2 = new Audio('/ahooga.mp3');
          audio2.volume = 0.5;
          audio2.play().catch(() => {});
        }, 3000);
      } catch {}
      // Stop flashing after 15 seconds
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      flashTimerRef.current = setTimeout(() => setFlashing(false), 15000);
    }
    prevCountRef.current = activeUsersCount;
  }, [activeUsersCount]);

  // Cleanup
  useEffect(() => () => { if (flashTimerRef.current) clearTimeout(flashTimerRef.current); }, []);

  // PWA Install prompt
  const [installPrompt, setInstallPrompt] = useState(null);
  const [appInstalled, setAppInstalled] = useState(false);
  const [showInstallGuide, setShowInstallGuide] = useState(false);

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

  useEffect(() => {
    if (isStandalone) {
      setAppInstalled(true);
      return;
    }
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => setAppInstalled(true));
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [isStandalone]);

  const handleInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const result = await installPrompt.userChoice;
      if (result.outcome === 'accepted') setAppInstalled(true);
      setInstallPrompt(null);
    } else {
      setShowInstallGuide(true);
    }
  };

  const [navOpen, setNavOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="border-b border-[#003B00] p-3 md:p-4" style={{ background: 'rgba(5, 5, 5, 0.9)' }}>
      <div className="max-w-[1920px] mx-auto">
        {/* TOP ROW - Logo/Title and Quick Navigation */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
          {/* Left - Logo and Title */}
          <div className="flex items-center gap-3">
            <img 
              src="/bitbot-logo.png" 
              alt="BitBot Logo" 
              className="object-contain h-20 w-20 md:h-40 md:w-40"
            />
            <div>
              <a href="/changelog" className="text-base md:text-2xl font-bold uppercase tracking-widest glow-text-strong glitch hover:opacity-80 transition-opacity cursor-pointer" data-text="BITBOT V3.9.32 [LIVE]" data-testid="header-version-link">
                BITBOT V3.9.32 [LIVE]
              </a>
              <div className="flex flex-wrap gap-1 mt-1">
                <Badge
                  className={`${tradingMode === 'live' ? 'mode-live' : 'mode-simulation'} rounded-none text-[10px] md:text-xs`}
                  data-testid="mode-badge"
                >
                  {tradingMode === 'simulation' ? 'SIMULATION' : tradingMode.toUpperCase()}
                </Badge>
                <Badge
                  className={`rounded-none text-[10px] md:text-xs font-bold ${
                    accessLevel === 'GOD' ? 'bg-red-900 text-red-200 border border-red-600' :
                    accessLevel === 'FAMILY' ? 'bg-purple-900 text-purple-200 border border-purple-600' :
                    accessLevel === 'PAID' ? 'bg-blue-900 text-blue-200 border border-blue-600' :
                    'bg-gray-800 text-gray-300 border border-gray-600'
                  }`}
                  data-testid="access-level-badge"
                >
                {accessLevel === 'GOD' ? 'GOD MODE' :
                 accessLevel === 'FAMILY' ? 'FAMILY' :
                 accessLevel === 'PAID' ? 'PAID TIER' :
                 'FREE TIER'}
              </Badge>
            </div>
          </div>
          </div>
          
          {/* Right - Nav: hamburger on mobile, inline on desktop */}
          <div className="flex items-center gap-2">
            {/* Install App button */}
            {!appInstalled && (
              <button
                onClick={handleInstall}
                className="border border-[#00FF41] bg-[#00FF41]/10 hover:bg-[#00FF41]/25 px-2 md:px-3 py-1 md:py-2 flex items-center gap-1.5 transition-colors animate-pulse hover:animate-none"
                data-testid="install-app-btn"
                title={installPrompt ? "Install BitBot as an app" : "Use your browser menu to install"}
              >
                <Download className="h-3.5 w-3.5 text-[#00FF41]" strokeWidth={2} />
                <span className="text-[10px] md:text-xs font-bold text-[#00FF41] tracking-wider">INSTALL APP</span>
              </button>
            )}

            {/* Always visible: Users count */}
            <a href="/traders" className={`border px-2 md:px-4 py-1 md:py-2 cursor-pointer transition-colors ${
              flashing 
                ? 'animate-pulse border-red-500 bg-red-900/30' 
                : 'border-[#00FF41] bg-[#001a00]/80 hover:bg-[#003B00]'
            }`} style={flashing ? { animation: 'userFlash 0.5s ease-in-out infinite' } : {}} data-testid="active-users-badge">
              <span className={`font-bold text-[10px] md:text-sm tracking-wider ${flashing ? '' : 'glow-text text-[#00FF41]'}`}
                style={flashing ? { animation: 'userTextFlash 0.5s ease-in-out infinite' } : {}}>
                {activeUsersCount == null
                  ? '… USERS'
                  : `${activeUsersCount} ${activeUsersCount === 1 ? 'USER' : 'USERS'}`}
              </span>
              <style>{`
                @keyframes userFlash {
                  0%, 100% { border-color: #00FF41; background-color: rgba(0,27,0,0.8); box-shadow: 0 0 15px rgba(0,255,65,0.4); }
                  50% { border-color: #ef4444; background-color: rgba(127,29,29,0.4); box-shadow: 0 0 15px rgba(239,68,68,0.4); }
                }
                @keyframes userTextFlash {
                  0%, 100% { color: #00FF41; }
                  50% { color: #ef4444; }
                }
              `}</style>
            </a>

            {/* Desktop nav buttons - hidden on mobile */}
            <div className="hidden md:flex flex-wrap items-center gap-2">
              <Button onClick={() => navigate('/users')} className="matrix-btn rounded-none text-[10px] md:text-xs h-7 md:h-8 border-purple-500 text-purple-500 hover:bg-purple-500 hover:text-black" data-testid="users-btn">
                <Users className="h-3 w-3 mr-1" strokeWidth={1.5} />TRADERS
              </Button>
              <Button onClick={() => navigate('/highscores')} className="matrix-btn rounded-none text-[10px] md:text-xs h-7 md:h-8 border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black" data-testid="highscores-btn">
                <Trophy className="h-3 w-3 mr-1" strokeWidth={1.5} />HIGH SCORES
              </Button>
              {isAdmin && (
                <Button onClick={() => navigate('/admin')} className="matrix-btn rounded-none text-[10px] md:text-xs h-7 md:h-8 border-emerald-500 text-emerald-500 hover:bg-emerald-500 hover:text-black" data-testid="admin-btn">
                  <Shield className="h-3 w-3 mr-1" strokeWidth={1.5} />ADMIN
                </Button>
              )}
              <Button onClick={onShare} className="matrix-btn rounded-none text-[10px] md:text-xs h-7 md:h-8 border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-black" data-testid="share-btn">
                <Share2 className="h-3 w-3 mr-1" strokeWidth={1.5} />SHARE
              </Button>
              <Button onClick={onLogout} className="matrix-btn rounded-none text-[10px] md:text-xs h-7 md:h-8 border-red-500 text-red-500 hover:bg-red-500 hover:text-black" data-testid="logout-btn">
                <LogOut className="h-3 w-3 mr-1" strokeWidth={1.5} />LOGOUT
              </Button>
            </div>

            {/* Mobile hamburger for top nav */}
            <div className="md:hidden relative">
              <button
                onClick={() => { setNavOpen(!navOpen); setMenuOpen(false); }}
                className="border border-[#00FF41] bg-[#001a00] p-2 text-[#00FF41]"
                data-testid="nav-hamburger"
              >
                {navOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
              {navOpen && (
                <div className="fixed left-3 right-3 mt-1 bg-[#0a0a0a] border border-[#003B00] shadow-[0_0_20px_rgba(0,255,65,0.1)] z-50" data-testid="nav-dropdown">
                  {[
                    { label: 'TRADERS', icon: Users, color: 'text-purple-400', path: '/users' },
                    { label: 'HIGH SCORES', icon: Trophy, color: 'text-yellow-400', path: '/highscores' },
                    ...(isAdmin ? [{ label: 'ADMIN', icon: Shield, color: 'text-emerald-400', path: '/admin' }] : []),
                    { label: 'SHARE', icon: Share2, color: 'text-blue-400', action: onShare },
                    { label: 'CHANGELOG', icon: FileText, color: 'text-[#00FF41]', path: '/changelog' },
                    { label: 'LOGOUT', icon: LogOut, color: 'text-red-400', action: onLogout },
                  ].map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.label}
                        onClick={() => { setNavOpen(false); item.path ? navigate(item.path) : item.action?.(); }}
                        className={`w-full flex items-center gap-2 px-3 py-2.5 text-xs ${item.color} hover:bg-[#001a00] border-b border-[#003B00]/30 last:border-0`}
                      >
                        <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* BOTTOM ROW - Controls and Navigation */}
        <div className="flex flex-wrap items-center justify-between gap-2 md:gap-4">
          {/* Left Section - Status indicators */}
          <div className="flex flex-wrap items-center gap-2 md:gap-4">
            {/* Connection Status */}
            <div className="flex items-center gap-2">
              <span
                className={`connection-dot ${isConnected ? 'connection-connected' : 'connection-disconnected'}`}
                data-testid="connection-status"
              />
              <span className="text-xs uppercase">{isConnected ? 'CONNECTED' : 'DISCONNECTED'}</span>
              {!isConnected && (
                <button
                  onClick={() => window.location.reload()}
                  className="text-[10px] px-2 py-0.5 border border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black"
                  title="Click to refresh and reconnect"
                >
                  RECONNECT
                </button>
              )}
            </div>

            <Separator orientation="vertical" className="h-6 bg-[#003B00]" />

            {/* Exchange Status */}
            {(activeExchange?.has_exchange || coinbaseConnected) && (
              <>
                <Badge 
                  className="rounded-none cursor-pointer hover:opacity-80"
                  style={{
                    backgroundColor: `${EXCHANGE_COLORS[activeExchange?.exchange] || '#00FF41'}20`,
                    color: EXCHANGE_COLORS[activeExchange?.exchange] || '#00FF41',
                    borderColor: EXCHANGE_COLORS[activeExchange?.exchange] || '#00FF41',
                    borderWidth: '1px',
                    borderStyle: 'solid'
                  }}
                  onClick={() => navigate('/config')}
                  data-testid="exchange-badge"
                >
                  <Building2 className="h-3 w-3 mr-1" strokeWidth={1.5} />
                  {activeExchange?.name || 'COINBASE'} {activeExchange?.connected !== false ? '✓' : ''}
                </Badge>
                <Separator orientation="vertical" className="h-6 bg-[#003B00]" />
              </>
            )}

            {/* Mode Toggle */}
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase text-[#008F11]">SIM</span>
              <Switch
                checked={tradingMode === 'live'}
                onCheckedChange={onModeChange}
                disabled={botStatus === 'running'}
                className="data-[state=checked]:bg-red-600"
                data-testid="mode-toggle"
              />
              <span className="text-xs uppercase text-red-500">LIVE</span>
            </div>

            <Separator orientation="vertical" className="h-6 bg-[#003B00]" />

            {/* Lifetime Profit */}
            <button
              onClick={onResetLifetimeProfit}
              className="flex items-center gap-2 px-3 py-1 border border-[#00FF41] bg-[#00FF41]/10 hover:bg-[#00FF41]/20 transition-colors cursor-pointer"
              data-testid="lifetime-profit"
              title="Click to reset profit counter"
            >
              <TrendingUp className="h-4 w-4 text-[#00FF41]" strokeWidth={1.5} />
              <div className="text-xs text-left">
                <div className="text-[#008F11]">PROFIT SINCE {lifetimeProfitResetDate}</div>
                <div className={`font-bold ${lifetimeProfit >= 0 ? 'text-[#00FF41]' : 'text-red-500'}`}>
                  {lifetimeProfit >= 0 ? '+' : ''}${(lifetimeProfit || 0).toLocaleString('en-US', { minimumFractionDigits: 6, maximumFractionDigits: 6 })}
                </div>
              </div>
            </button>

            <Separator orientation="vertical" className="h-6 bg-[#003B00]" />

            {/* Run Timer */}
            <div className="flex items-center gap-4 px-3 py-1 border border-[#008F11] bg-[#003B00]/30" data-testid="run-timer">
              <Timer className="h-4 w-4 text-[#00FF41]" strokeWidth={1.5} />
              <div className="text-xs">
                <div className="text-[#008F11]">RUN TIME</div>
                <div className="font-bold text-[#00FF41] font-mono">
                  {runTimer.isLoaded ? formatElapsedTime(runTimer.elapsedSeconds) : '--:--:--'}
                </div>
              </div>
              <Separator orientation="vertical" className="h-8 bg-[#003B00]" />
              <div className="text-xs">
                <div className="text-[#008F11]">SESSION P/L</div>
                <div className={`font-bold ${runTimer.currentProfit >= 0 ? 'text-[#00FF41]' : 'text-red-500'}`}>
                  {runTimer.isLoaded 
                    ? (runTimer.currentProfit >= 0 
                        ? `+$${runTimer.currentProfit.toFixed(2)}` 
                        : `-$${Math.abs(runTimer.currentProfit).toFixed(2)}`)
                    : '--'}
                </div>
              </div>
              <Separator orientation="vertical" className="h-8 bg-[#003B00]" />
              <div className="text-xs">
                <div className="text-[#008F11]">$/HR</div>
                <div className={`font-bold ${runTimer.profitPerHour >= 0 ? 'text-[#00FF41]' : 'text-red-500'}`}>
                  {runTimer.isLoaded 
                    ? (runTimer.profitPerHour >= 0 
                        ? `+$${runTimer.profitPerHour.toFixed(4)}` 
                        : `-$${Math.abs(runTimer.profitPerHour).toFixed(4)}`)
                    : '--'}
                </div>
              </div>
              {botStatus === 'running' && onResetSessionBalance && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onResetSessionBalance}
                  className="ml-2 text-[10px] text-yellow-500 hover:text-yellow-400 hover:bg-yellow-900/20 px-2 py-1 h-6"
                  title="Reset session P/L to $0"
                >
                  RESET P/L
                </Button>
              )}
            </div>
          </div>

          {/* Right Section - User Actions: dropdown on mobile, inline on desktop */}
          <div className="flex items-center gap-2">
            {/* Always visible: user avatar + TRADE btn */}
            {user && (
              <>
                <button 
                  onClick={() => navigate(`/profile/${user.user_id}`)}
                  className="flex items-center gap-2 px-2 py-1 border border-[#003B00] bg-[#003B00]/20 hover:border-[#00FF41] hover:bg-[#003B00]/40 transition-colors cursor-pointer"
                  data-testid="user-profile-btn"
                >
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="h-6 w-6 rounded-full border border-[#00FF41]" />
                  ) : (
                    <User className="h-4 w-4 text-[#00FF41]" strokeWidth={1.5} />
                  )}
                  <span className="text-xs text-[#00FF41] hidden sm:inline">{user.name?.split(' ')[0]}</span>
                </button>
              </>
            )}

            {/* Hide Clear History button in live mode */}
            {tradingMode !== 'live' && (
              <Button onClick={onClearTradeHistory} className="matrix-btn rounded-none text-xs h-8 hidden md:flex" data-testid="clear-history-btn">
                <Trash2 className="h-3 w-3 mr-1" strokeWidth={1.5} />CLEAR
              </Button>
            )}

            {/* Desktop feature buttons - hidden on mobile */}
            {user && (
              <div className="hidden md:flex flex-wrap items-center gap-2">
                <Button onClick={onManualTrade} className="matrix-btn rounded-none text-xs h-8 border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black" data-testid="manual-trade-btn">
                  <ArrowDownUp className="h-3 w-3 mr-1" strokeWidth={1.5} />TRADE
                </Button>
                <Button onClick={() => navigate('/subscription')} className="matrix-btn rounded-none text-xs h-8 border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-black" data-testid="subscription-btn">
                  <Zap className="h-3 w-3 mr-1" strokeWidth={1.5} />SUBSCRIBE
                </Button>
                <Button onClick={() => navigate('/config')} className="matrix-btn rounded-none text-xs h-8" data-testid="settings-btn">
                  <Settings className="h-3 w-3 mr-1" strokeWidth={1.5} />CONFIG
                </Button>
                <Button onClick={() => navigate('/messages')} className="matrix-btn rounded-none text-xs h-8 border-cyan-500 text-cyan-500 hover:bg-cyan-500 hover:text-black relative" data-testid="messages-btn">
                  <Mail className="h-3 w-3 mr-1" strokeWidth={1.5} />MESSAGES
                  {unreadMessages > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center">
                      {unreadMessages > 9 ? '9+' : unreadMessages}
                    </span>
                  )}
                </Button>
                {accessLevel === 'GOD' && (
                  <Button onClick={() => navigate('/monetization')} className="matrix-btn rounded-none text-xs h-8 border-green-400 text-green-400 hover:bg-green-400 hover:text-black" data-testid="monetization-btn">
                    <DollarSign className="h-3 w-3 mr-1" strokeWidth={1.5} />MONETIZE
                  </Button>
                )}
                <Button onClick={() => navigate('/stats')} className="matrix-btn rounded-none text-xs h-8 border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black" data-testid="stats-btn">
                  <BarChart3 className="h-3 w-3 mr-1" strokeWidth={1.5} />STATS
                </Button>
                <Button onClick={() => navigate('/music')} className="matrix-btn rounded-none text-xs h-8 border-pink-500 text-pink-500 hover:bg-pink-500 hover:text-black" data-testid="music-nav-btn">
                  <Music className="h-3 w-3 mr-1" strokeWidth={1.5} />MUSIC
                </Button>
                <Button onClick={() => navigate('/backtest')} className="matrix-btn rounded-none text-xs h-8 border-cyan-500 text-cyan-500 hover:bg-cyan-500 hover:text-black" data-testid="backtest-nav-btn">
                  <Target className="h-3 w-3 mr-1" strokeWidth={1.5} />BACKTEST
                </Button>
                <Button onClick={() => navigate('/tax')} className="matrix-btn rounded-none text-xs h-8 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-black" data-testid="tax-nav-btn">
                  <FileText className="h-3 w-3 mr-1" strokeWidth={1.5} />TAX
                </Button>
                <Button onClick={() => navigate('/timeline')} className="matrix-btn rounded-none text-xs h-8 border-pink-500 text-pink-400 hover:bg-pink-500 hover:text-black" data-testid="timeline-nav-btn">
                  <PlayCircle className="h-3 w-3 mr-1" strokeWidth={1.5} />REPLAY
                </Button>
              </div>
            )}

            {/* Mobile feature menu */}
            {user && (
              <div className="md:hidden relative">
                <button
                  onClick={() => { setMenuOpen(!menuOpen); setNavOpen(false); }}
                  className="border border-[#008F11] bg-[#001a00] px-2 py-1.5 text-[#00FF41] flex items-center gap-1 text-xs"
                  data-testid="feature-menu-btn"
                >
                  <ChevronDown className={`h-4 w-4 transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
                  MENU
                </button>
                {menuOpen && (
                  <div className="fixed left-3 right-3 top-auto mt-1 bg-[#0a0a0a] border border-[#003B00] shadow-[0_0_20px_rgba(0,255,65,0.1)] z-50 max-h-[50vh] overflow-y-auto" style={{ marginTop: '4px' }} data-testid="feature-dropdown">
                    {[
                      { label: 'TRADERS', icon: Users, color: 'text-purple-400', path: '/users' },
                      { label: 'TRADE', icon: ArrowDownUp, color: 'text-yellow-400', action: onManualTrade },
                      { label: 'CONFIG', icon: Settings, color: 'text-[#00FF41]', path: '/config' },
                      { label: 'MESSAGES', icon: Mail, color: 'text-cyan-400', path: '/messages', badge: unreadMessages },
                      { label: 'SUBSCRIBE', icon: Zap, color: 'text-blue-400', path: '/subscription' },
                      { label: 'STATS', icon: BarChart3, color: 'text-yellow-400', path: '/stats' },
                      ...(accessLevel === 'GOD' ? [{ label: 'MONETIZE', icon: DollarSign, color: 'text-green-400', path: '/monetization' }] : []),
                      { label: 'HIGH SCORES', icon: Trophy, color: 'text-yellow-400', path: '/highscores' },
                      { label: 'MUSIC', icon: Music, color: 'text-pink-400', path: '/music' },
                      { label: 'BACKTEST', icon: Target, color: 'text-cyan-400', path: '/backtest' },
                      { label: 'TAX', icon: FileText, color: 'text-orange-400', path: '/tax' },
                      { label: 'REPLAY', icon: PlayCircle, color: 'text-pink-400', path: '/timeline' },
                      ...(tradingMode !== 'live' ? [{ label: 'CLEAR HISTORY', icon: Trash2, color: 'text-gray-400', action: onClearTradeHistory }] : []),
                    ].map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.label}
                          onClick={() => { setMenuOpen(false); item.path ? navigate(item.path) : item.action?.(); }}
                          className={`w-full flex items-center gap-2 px-3 py-2.5 text-xs ${item.color} hover:bg-[#001a00] border-b border-[#003B00]/30 last:border-0`}
                        >
                          <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
                          {item.label}
                          {item.badge > 0 && (
                            <span className="ml-auto bg-red-500 text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center">
                              {item.badge > 9 ? '9+' : item.badge}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Install Guide - Platform Aware */}
      {showInstallGuide && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center" onClick={() => setShowInstallGuide(false)}>
          <div className="bg-[#0a0a0a] border border-[#00FF41] mx-4 p-5 max-w-sm w-full" onClick={e => e.stopPropagation()} data-testid="install-guide">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-bold text-[#00FF41] uppercase tracking-wider">Install BitBot</span>
              <button onClick={() => setShowInstallGuide(false)} className="text-gray-500 hover:text-white"><X className="h-4 w-4" /></button>
            </div>
            {isIOS ? (
              <div className="space-y-3 text-sm text-gray-300">
                <p><span className="text-[#00FF41] font-bold">1.</span> Tap the <span className="text-blue-400 font-bold">Share</span> button <span className="text-blue-400">(↑)</span> at the bottom of Safari</p>
                <p><span className="text-[#00FF41] font-bold">2.</span> Scroll down and tap <span className="text-white font-bold">"Add to Home Screen"</span></p>
                <p><span className="text-[#00FF41] font-bold">3.</span> Tap <span className="text-white font-bold">"Add"</span> in the top right</p>
              </div>
            ) : (
              <div className="space-y-3 text-sm text-gray-300">
                <p><span className="text-[#00FF41] font-bold">Chrome / Edge:</span></p>
                <p><span className="text-[#00FF41] font-bold">1.</span> Click the <span className="text-white font-bold">install icon</span> in the address bar (⊕ or ↓), OR</p>
                <p><span className="text-[#00FF41] font-bold">2.</span> Click the <span className="text-white font-bold">⋮ menu</span> (top right) → <span className="text-white font-bold">"Install BitBot"</span> or <span className="text-white font-bold">"Install app"</span></p>
                <p className="text-gray-500 text-xs mt-2">Firefox: Not supported for PWA install. Use Chrome or Edge.</p>
              </div>
            )}
            <p className="text-[10px] text-gray-500 mt-3">BitBot will launch as a full-screen standalone app.</p>
          </div>
        </div>
      )}
    </header>
  );
};

export default DashboardHeader;
